import React, { useState, useEffect } from 'react';
import { IDocument } from '@cyntler/react-doc-viewer';

import { useTheme } from '@mui/material/styles';
import { Box, Tooltip, IconButton, Typography } from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';

import useLocales from '../../hooks/useLocales';
import { getFileType } from '../../utils/getFileFormat';
import SvgIconStyle from '../../components/SvgIconStyle';
import useNotifications from '../../hooks/useNotifications';
import documentService from '../../services/documentService';
import { AccompanyingDocumentProps } from '../../@types/documents/types';
import viewerFileDownload from '../../utils/documents/viewerFileDownload';
import { confirmAccDocumentDownload } from '../../redux/slices/documentHistory';
import DocumentAccompanyingDocumentViewerDialog from './DocumentAccompanyingDocumentViewerDialog';

// ----------------------------------------------------------------------

type DocumentAccompanyingDocumentElementProps = {
  accompanyingDocument: AccompanyingDocumentProps;
  getAccompanyingDocumentsList: () => void;
  setIsLoading: (isLoading: boolean) => void;
  wrapperWidth: number;
  handleHistoryUpdate: () => void;
};

const DocumentAccompanyingDocumentElement: React.FC<DocumentAccompanyingDocumentElementProps> = ({
  accompanyingDocument,
  getAccompanyingDocumentsList,
  setIsLoading,
  wrapperWidth,
  handleHistoryUpdate,
}) => {
  const { t } = useLocales();
  const theme = useTheme();
  const [docs, setDocs] = useState<IDocument[]>([]);
  const [isViewerDialogOpen, setIsViewerDialogOpen] = useState<boolean>(false);
  const [textWidth, setTextWidth] = useState<string>('295px');
  const { enqueueSuccess, enqueueError } = useNotifications();

  const WIDTH_OF_OTHER_ELEMENTS = 190;

  const srcImg = (name: string) => {
    const extension = getFileType(name);
    return `/icons/icon_${extension}.svg`;
  };

  const handleDownloadAccompanyingDocument = async () => {
    try {
      await viewerFileDownload(docs[0]);
      await confirmAccDocumentDownload(accompanyingDocument.documentId, accompanyingDocument.name);

      enqueueSuccess(t('documents.snackbar.documentSuccessfullyDownloaded'), {
        documentName: accompanyingDocument.name,
      });
    } catch (error) {
      enqueueError(error.message);
    } finally {
      handleHistoryUpdate();
    }
  };

  const handleDeleteAccompanyingDocument = async () => {
    try {
      setIsLoading(true);

      await documentService.removeAccompanyingDocument(
        accompanyingDocument.documentId,
        accompanyingDocument.id
      );

      enqueueSuccess(
        t('documents.accompanyingDocumentSuccessfullyDeleted', {
          docName: accompanyingDocument.name,
        })
      );
    } catch (error) {
      enqueueError(error.message);
    } finally {
      getAccompanyingDocumentsList();
      handleHistoryUpdate();
      setIsLoading(false);
    }
  };

  const handleToggleHasSeen = async () => {
    try {
      setIsLoading(true);

      await documentService.toggleAccompanyingDocumentHasSeen(
        accompanyingDocument.documentId,
        accompanyingDocument.id
      );

      enqueueSuccess(
        accompanyingDocument.seen
          ? t('documents.accompanyingDocumentSuccessfullyMarkedUnseen', {
              docName: accompanyingDocument.name,
            })
          : t('documents.accompanyingDocumentSuccessfullyMarkedSeen', {
              docName: accompanyingDocument.name,
            })
      );
    } catch (error) {
      enqueueError(error.message);
    } finally {
      getAccompanyingDocumentsList();
      handleHistoryUpdate();
      setIsLoading(false);
    }
  };

  const handleOpenViewerDialog = () => {
    setIsViewerDialogOpen(true);
  };

  useEffect(() => {
    setDocs([
      {
        uri: accompanyingDocument.accessId,
        fileType: getFileType(accompanyingDocument.name),
        fileData: accompanyingDocument.accessId,
        fileName: accompanyingDocument.name,
      },
    ]);
  }, [accompanyingDocument]);

  useEffect(() => {
    setTextWidth(`${wrapperWidth - WIDTH_OF_OTHER_ELEMENTS}px`);
  }, [wrapperWidth]);

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 2,
      }}
    >
      <img
        height={36}
        style={{ paddingLeft: '8px' }}
        src={srcImg(accompanyingDocument.name)}
        alt={getFileType(accompanyingDocument.name)}
      />

      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          cursor: 'pointer',
          '&:hover': {
            color: theme.palette.primary.main,
          },
        }}
        onClick={handleOpenViewerDialog}
      >
        <Typography
          data-testid="accompanying-document-name"
          sx={{ width: textWidth }}
          variant="subtitle1"
          noWrap
        >
          {accompanyingDocument.name}
        </Typography>

        <Typography sx={{ width: textWidth }} variant="caption" noWrap>
          {`(${accompanyingDocument.owner}, ${new Date(accompanyingDocument.uploadDate).toLocaleString('lt')})`}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'right', alignItems: 'center' }}>
        {!accompanyingDocument.seen && (
          <Tooltip
            title={t('documents.markAsSeen')}
            placement="top"
            arrow
            slotProps={{
              popper: {
                modifiers: [
                  {
                    name: 'offset',
                    options: {
                      offset: [0, -14],
                    },
                  },
                ],
              },
            }}
          >
            <IconButton onClick={handleToggleHasSeen}>
              <VisibilityOffOutlinedIcon id="VisibilityOffOutlinedIcon" />
            </IconButton>
          </Tooltip>
        )}

        {accompanyingDocument.seen && (
          <Tooltip
            title={t('documents.markAsUnseen')}
            placement="top"
            arrow
            slotProps={{
              popper: {
                modifiers: [
                  {
                    name: 'offset',
                    options: {
                      offset: [0, -14],
                    },
                  },
                ],
              },
            }}
          >
            <IconButton onClick={handleToggleHasSeen}>
              <VisibilityOutlinedIcon id="VisibilityOutlinedIcon" />
            </IconButton>
          </Tooltip>
        )}

        <IconButton onClick={handleDownloadAccompanyingDocument} id="4eq843hg">
          <SvgIconStyle
            src="/icons/icon_download.svg"
            sx={{
              width: '22px',
              height: '22px',
              color: 'inherit',
            }}
          />
        </IconButton>

        <IconButton onClick={handleDeleteAccompanyingDocument} id="4eq843hg">
          <SvgIconStyle
            src="/icons/icon_trashbin.svg"
            sx={{
              width: '22px',
              height: '22px',
              color: 'inherit',
            }}
          />
        </IconButton>
      </Box>

      <DocumentAccompanyingDocumentViewerDialog
        isViewerDialogOpen={isViewerDialogOpen}
        setIsViewerDialogOpen={setIsViewerDialogOpen}
        docs={docs}
        accompanyingDocument={accompanyingDocument}
        handleToggleHasSeen={handleToggleHasSeen}
      />
    </Box>
  );
};

export default DocumentAccompanyingDocumentElement;
